/* VP Druckfarben (09.10.2026) — gemeinsame Helfer fuer die Editoren „Motiv gestalten" (Engine verpackung, Konfigurator flach).
   Anlass: Feedback Jessica Cox 08.10.2026: Farben im Editor sind RGB und wirken leuchtender als gedruckt, zu kleine Logos sollen
   beim Einfuegen auffallen (Vektor oder mindestens 300 dpi), ein paar Schriften mehr.

   1. CMYK Simulation (Softproof): Druckmodell fuer Offset auf gestrichenem Papier (ISO Coated v2 / FOGRA39, Volltoene nach ISO 12647-2),
      Yule-Nielsen Neugebauer mit n = 2 (Tonwertzunahme), Gesamtfarbauftrag 300 %. Daraus die Farbraumgrenze je Buntton und Helligkeit in OKLab.
      Bildschirmfarben innerhalb der Grenze bleiben unveraendert, Farben ausserhalb werden zum Spitzenpunkt (Cusp) des Bunttons hin auf die
      Grenze geschoben (wie relativ farbmetrisch). hex(), rgb() fuer Volltonfarben, bild() fuer Fotos und Logos (33er LUT, Ergebnis als data URL).
      cmyk() schaetzt die Separation (Prozentwerte) fuer die Druckvorstufe. Simulation, kein ICC Profil: Abweichungen im Bereich weniger Delta E.
   2. Aufloesung: dpi(px, mm), stufe(dpi) mit 300 dpi Ziel, maxMm(px).
   3. Schriften: 21 Google Fonts (OFL), selbst gehostet als Theme Assets vp-font-<id>.woff2 (kein Abruf bei Google, DSGVO).
      laden() meldet sie per FontFace an, fontFaceCss() bettet sie fuer SVG zu PNG Exporte als data URL ein.
   window.VPDruckfarben */
window.VPDruckfarben = (function () {
  'use strict';
  // ---------------------------------------------------------------- Farbraeume
  const lin = (v) => (v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4));
  const gam = (v) => (v <= 0.0031308 ? 12.92 * v : 1.055 * Math.pow(v, 1 / 2.4) - 0.055);
  const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);
  // CIELab (Weiss = Papier, relativ farbmetrisch) -> XYZ D65
  const WX = 0.95047, WY = 1, WZ = 1.08883;
  function labXyz(L, a, b) {
    const fy = (L + 16) / 116, fx = fy + a / 500, fz = fy - b / 200, e = 216 / 24389, k = 24389 / 27;
    const f = (t) => (t * t * t > e ? t * t * t : (116 * t - 16) / k);
    return [WX * f(fx), WY * (L > k * e ? fy * fy * fy : L / k), WZ * f(fz)];
  }
  // XYZ D65 <-> lineares sRGB
  const xyzLin = (x, y, z) => [3.2404542 * x - 1.5371385 * y - 0.4985314 * z, -0.969266 * x + 1.8760108 * y + 0.041556 * z, 0.0556434 * x - 0.2040259 * y + 1.0572252 * z];
  // lineares sRGB <-> OKLab (Bjoern Ottosson 2020)
  function linOk(r, g, b) {
    const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b), m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b), s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
    return [0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s, 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s, 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s];
  }
  function okLin(L, a, b) {
    const l = L + 0.3963377774 * a + 0.2158037573 * b, m = L - 0.1055613458 * a - 0.0638541728 * b, s = L - 0.0894841775 * a - 1.291485548 * b;
    const l3 = l * l * l, m3 = m * m * m, s3 = s * s * s;
    return [4.0767416621 * l3 - 3.3077115913 * m3 + 0.2309699292 * s3, -1.2684380046 * l3 + 2.6097574011 * m3 - 0.3413193965 * s3, -0.0041960863 * l3 - 0.7034186147 * m3 + 1.707614701 * s3];
  }
  const hexRgb = (hex) => { const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || '').trim()); if (!m) return null; const n = parseInt(m[1], 16); return [(n >> 16) & 255, (n >> 8) & 255, n & 255]; };
  const rgbHex = (r, g, b) => '#' + [r, g, b].map((v) => Math.round(v).toString(16).padStart(2, '0')).join('').toUpperCase();
  const rgbOk = (r, g, b) => linOk(lin(r / 255), lin(g / 255), lin(b / 255));
  const okRgb = (L, a, b) => okLin(L, a, b).map((v) => clamp01(gam(clamp01(v))) * 255);

  // ---------------------------------------------------------------- Druckmodell ISO Coated v2 (FOGRA39)
  // Volltoene und Ueberdrucke als CIELab nach ISO 12647-2 (Papier als Weiss). Reihenfolge Bit 0 = C, Bit 1 = M, Bit 2 = Y.
  const PRIM_LAB = [[100, 0, 0], [55, -37, -50], [48, 74, -3], [24, 22, -46], [89, -5, 93], [50, -65, 27], [47, 68, 48], [23, 0, 0]];
  const K_LAB = [16, 0, 0], N = 2, TAC = 3.0, BODEN = 0.008; // Yule-Nielsen n, Gesamtfarbauftrag, Oberflaechenreflexion (dunkelster Punkt etwa L 9)
  const PRIM = PRIM_LAB.map((p) => labXyz(p[0], p[1], p[2]).map((v) => Math.pow(v, 1 / N)));
  const KXYZ = labXyz(K_LAB[0], K_LAB[1], K_LAB[2]), KF = [KXYZ[0] / WX, KXYZ[1] / WY, KXYZ[2] / WZ].map((v) => Math.pow(v, 1 / N));
  // CMYK (0..1) -> XYZ D65
  function druck(c, m, y, k) {
    const x = [0, 0, 0];
    for (let i = 0; i < 8; i++) {
      const w = (i & 1 ? c : 1 - c) * (i & 2 ? m : 1 - m) * (i & 4 ? y : 1 - y); if (!w) continue;
      x[0] += w * PRIM[i][0]; x[1] += w * PRIM[i][1]; x[2] += w * PRIM[i][2];
    }
    const W = [WX, WY, WZ];
    return x.map((v, j) => { const o = Math.pow(v * ((1 - k) + k * KF[j]), N); return o + BODEN * (W[j] - o); });
  }
  const druckOk = (c, m, y, k) => { const x = druck(c, m, y, k); const l = xyzLin(x[0], x[1], x[2]); return linOk(l[0], l[1], l[2]); };

  // ---------------------------------------------------------------- Farbraumgrenze in OKLCh
  const HB = 72, LB = 51; // Buntton 5 Grad, Helligkeit 0,02
  let GRENZE = null, LMIN = 0;
  function grenzeBauen() {
    if (GRENZE) return;
    GRENZE = new Float32Array(HB * LB).fill(-1); LMIN = 1;
    const st = 32, merke = (o) => {
      const C = Math.hypot(o[1], o[2]); let h = Math.atan2(o[2], o[1]) * 180 / Math.PI; if (h < 0) h += 360;
      if (o[0] < LMIN) LMIN = o[0];
      const hi = Math.round(h / 5) % HB, li = Math.round(o[0] * (LB - 1)); if (li < 0 || li >= LB) return;
      if (C > GRENZE[hi * LB + li]) GRENZE[hi * LB + li] = C;
    };
    // Aussenflaechen des CMY Wuerfels (mindestens ein Kanal 0 oder 100 %) mit Schwarz in 10er Schritten, innerhalb TAC
    for (let kk = 0; kk <= 10; kk++) { const k = kk / 10;
      for (let f = 0; f < 6; f++) for (let i = 0; i <= st; i++) for (let j = 0; j <= st; j++) {
        const u = i / st, v = j / st, fest = f < 3 ? 0 : 1, ax = f % 3, cmy = ax === 0 ? [fest, u, v] : ax === 1 ? [u, fest, v] : [u, v, fest];
        if (cmy[0] + cmy[1] + cmy[2] + k > TAC + 1e-9) continue;
        merke(druckOk(cmy[0], cmy[1], cmy[2], k));
      }
    }
    // Luecken je Buntton linear fuellen, zu Papierweiss (L 1) und unter dem dunkelsten Punkt auf 0
    for (let h = 0; h < HB; h++) {
      const row = GRENZE.subarray(h * LB, h * LB + LB); row[LB - 1] = Math.max(0, row[LB - 1]);
      let last = -1;
      for (let l = 0; l < LB; l++) { if (row[l] < 0) continue; if (last < 0) { for (let q = 0; q < l; q++) row[q] = q / (LB - 1) < LMIN ? 0 : row[l]; } else for (let q = last + 1; q < l; q++) row[q] = row[last] + (row[l] - row[last]) * (q - last) / (l - last); last = l; }
    }
  }
  function cmax(L, h) {
    const hf = ((h % 360) + 360) % 360 / 5, h0 = Math.floor(hf) % HB, h1 = (h0 + 1) % HB, th = hf - Math.floor(hf);
    const lf = clamp01(L) * (LB - 1), l0 = Math.floor(lf), l1 = Math.min(LB - 1, l0 + 1), tl = lf - l0;
    const g = (hi, li) => GRENZE[hi * LB + li];
    return (g(h0, l0) * (1 - tl) + g(h0, l1) * tl) * (1 - th) + (g(h1, l0) * (1 - tl) + g(h1, l1) * tl) * th;
  }
  const drin = (L, C, h) => L >= LMIN - 1e-4 && L <= 1 + 1e-4 && C <= cmax(L, h) + 0.002;
  // OKLab -> naechste druckbare OKLab Farbe. Wie relativ farbmetrisch: kuerzester Abstand zur Farbraumgrenze, Buntton darf sich etwas
  // verschieben (sRGB Gelb liegt bei 110 Grad, Prozessgelb bei 98 Grad; mit festem Buntton wuerde Gelb blass statt sattgelb).
  // Abstand im OKLab mit Gewicht auf dem Buntton (WH), damit Blau nicht nach Violett und Rot nicht nach Orange kippt.
  const WH = 1.6;
  function abstand(L, C, h, L2, C2, h2) { const dh = (h - h2) * Math.PI / 180, dH2 = 2 * C * C2 * (1 - Math.cos(dh)); return (L - L2) * (L - L2) + (C - C2) * (C - C2) + WH * WH * dH2; }
  function abbilden(L, a, b) {
    grenzeBauen();
    const C = Math.hypot(a, b), h = Math.atan2(b, a) * 180 / Math.PI;
    if (drin(L, C, h)) return [L, a, b];
    if (C < 0.02) { const L2 = Math.min(1, Math.max(LMIN, L)); return [L2, a, b]; } // unbunt: nur Helligkeit begrenzen
    // grob: Raster der Grenze (Buntton +-45 Grad, alle Helligkeiten), fein: zweimal lokal mit bilinearer Grenze
    let best = Infinity, bL = L, bh = h;
    for (let dh = -45; dh <= 45; dh += 5) for (let li = 0; li < LB; li++) { const L2 = li / (LB - 1); if (L2 < LMIN) continue; const C2 = cmax(L2, h + dh), d = abstand(L, C, h, L2, C2, h + dh); if (d < best) { best = d; bL = L2; bh = h + dh; } }
    for (const [sL, sh] of [[0.01, 2], [0.002, 0.5]]) { const L0 = bL, h0 = bh; for (let i = -3; i <= 3; i++) for (let j = -3; j <= 3; j++) { const L2 = Math.min(1, Math.max(LMIN, L0 + i * sL)), h2 = h0 + j * sh, C2 = cmax(L2, h2), d = abstand(L, C, h, L2, C2, h2); if (d < best) { best = d; bL = L2; bh = h2; } } }
    const C2 = Math.max(0, cmax(bL, bh) - 0.001), r = bh * Math.PI / 180; return [bL, C2 * Math.cos(r), C2 * Math.sin(r)];
  }

  // ---------------------------------------------------------------- Volltonfarben
  const CACHE = new Map();
  function info(hex) {
    const key = String(hex || '').toUpperCase(); if (CACHE.has(key)) return CACHE.get(key);
    const rgb = hexRgb(key); if (!rgb) return null;
    const o = rgbOk(rgb[0], rgb[1], rgb[2]), p = abbilden(o[0], o[1], o[2]), s = okRgb(p[0], p[1], p[2]);
    const r = { hex: key, druck: rgbHex(s[0], s[1], s[2]), abweichung: Math.round(Math.hypot(o[0] - p[0], o[1] - p[1], o[2] - p[2]) * 1000) / 10 };
    r.ausserhalb = r.abweichung > 3; CACHE.set(key, r); return r;
  }
  const hex = (h) => { const i = info(h); return i ? i.druck : h; };

  // Separation fuer die Druckvorstufe: Schwarzaufbau nach Helligkeit (mittlerer GCR), CMY per gedaempftem Gauss-Newton auf das Druckmodell
  function cmyk(h) {
    const i = info(h); if (!i) return null; if (i.cmyk) return i.cmyk;
    const rgb = hexRgb(i.druck), z = rgbOk(rgb[0], rgb[1], rgb[2]);
    const r = rgb[0] / 255, g = rgb[1] / 255, b = rgb[2] / 255, k0 = 1 - Math.max(r, g, b);
    const start = k0 < 1 ? [(1 - r - k0) / (1 - k0), (1 - g - k0) / (1 - k0), (1 - b - k0) / (1 - k0)] : [0, 0, 0];
    const tac = (v, k) => { const sum = v[0] + v[1] + v[2]; return sum + k > TAC ? v.map((x) => x * Math.max(0, TAC - k) / sum) : v; };
    const fehler = (v, k) => { const o = druckOk(v[0], v[1], v[2], k); return [o[0] - z[0], o[1] - z[1], o[2] - z[2]]; };
    const loesen = (k) => {
      let v = tac(start.slice(), k), e = fehler(v, k), n = Math.hypot(e[0], e[1], e[2]);
      for (let it = 0; it < 40 && n > 1e-4; it++) {
        const J = [0, 1, 2].map((j) => { const w = v.slice(); w[j] = clamp01(w[j] + (w[j] > 0.98 ? -1e-3 : 1e-3)); const d = (w[j] - v[j]) || 1e-3, f = fehler(w, k); return [(f[0] - e[0]) / d, (f[1] - e[1]) / d, (f[2] - e[2]) / d]; });
        // J ist spaltenweise (je Kanal), loesen J * dv = -e mit Cramer
        const A = [[J[0][0], J[1][0], J[2][0]], [J[0][1], J[1][1], J[2][1]], [J[0][2], J[1][2], J[2][2]]];
        const det = (M) => M[0][0] * (M[1][1] * M[2][2] - M[1][2] * M[2][1]) - M[0][1] * (M[1][0] * M[2][2] - M[1][2] * M[2][0]) + M[0][2] * (M[1][0] * M[2][1] - M[1][1] * M[2][0]);
        const D = det(A); if (Math.abs(D) < 1e-12) break;
        const dv = [0, 1, 2].map((c) => det(A.map((row, ri) => row.map((x, ci) => (ci === c ? -e[ri] : x)))) / D);
        let s = 1, w, ew, nw;
        do { w = tac(v.map((x, j) => clamp01(x + s * dv[j])), k); ew = fehler(w, k); nw = Math.hypot(ew[0], ew[1], ew[2]); s /= 2; } while (nw > n && s > 1 / 64);
        if (nw >= n) break; v = w; e = ew; n = nw;
      }
      return { v, n, k };
    };
    const kg = Math.max(0, (k0 - 0.35) / 0.65);
    // Kandidaten fuer Schwarz; gewaehlt wird der kleinste Restfehler, bei Gleichstand (unter 0,5 Delta E) der Schwarzaufbau nach Regel
    let best = null; [kg, Math.min(1, kg + 0.15), kg * 0.5, Math.max(0, kg - 0.3), 0].forEach((k) => { const r1 = loesen(k); r1.w = Math.max(r1.n, 0.005) + Math.abs(k - kg) * 0.002; if (!best || r1.w < best.w) best = r1; });
    const pc = (x) => Math.round(x * 100);
    i.cmyk = { c: pc(best.v[0]), m: pc(best.v[1]), y: pc(best.v[2]), k: pc(best.k) };
    return i.cmyk;
  }
  const cmykText = (h) => { const c = cmyk(h); return c ? `C ${c.c} M ${c.m} Y ${c.y} K ${c.k}` : ''; };

  // ---------------------------------------------------------------- Bilder (33er LUT, trilinear)
  const S3 = 33; let LUT = null;
  function lutBauen() {
    if (LUT) return; grenzeBauen(); LUT = new Uint8ClampedArray(S3 * S3 * S3 * 3); let i = 0;
    for (let r = 0; r < S3; r++) for (let g = 0; g < S3; g++) for (let b = 0; b < S3; b++) {
      const o = rgbOk(r * 255 / (S3 - 1), g * 255 / (S3 - 1), b * 255 / (S3 - 1)), p = abbilden(o[0], o[1], o[2]), s = okRgb(p[0], p[1], p[2]);
      LUT[i++] = s[0]; LUT[i++] = s[1]; LUT[i++] = s[2];
    }
  }
  function pixel(d) {
    lutBauen(); const q = (S3 - 1) / 255, L = LUT, idx = (r, g, b) => ((r * S3 + g) * S3 + b) * 3;
    for (let p = 0; p < d.length; p += 4) {
      if (!d[p + 3]) continue;
      const fr = d[p] * q, fg = d[p + 1] * q, fb = d[p + 2] * q, r0 = Math.min(S3 - 2, fr | 0), g0 = Math.min(S3 - 2, fg | 0), b0 = Math.min(S3 - 2, fb | 0), tr = fr - r0, tg = fg - g0, tb = fb - b0;
      for (let c = 0; c < 3; c++) {
        const c00 = L[idx(r0, g0, b0) + c] * (1 - tb) + L[idx(r0, g0, b0 + 1) + c] * tb, c01 = L[idx(r0, g0 + 1, b0) + c] * (1 - tb) + L[idx(r0, g0 + 1, b0 + 1) + c] * tb;
        const c10 = L[idx(r0 + 1, g0, b0) + c] * (1 - tb) + L[idx(r0 + 1, g0, b0 + 1) + c] * tb, c11 = L[idx(r0 + 1, g0 + 1, b0) + c] * (1 - tb) + L[idx(r0 + 1, g0 + 1, b0 + 1) + c] * tb;
        d[p + c] = (c00 * (1 - tg) + c01 * tg) * (1 - tr) + (c10 * (1 - tg) + c11 * tg) * tr;
      }
    }
  }
  // Canvas in place umrechnen (fuer Canvas Editoren); false, wenn die Pixel nicht lesbar sind
  function canvas(cv) { try { const x = cv.getContext('2d'), d = x.getImageData(0, 0, cv.width, cv.height); pixel(d.data); x.putImageData(d, 0, 0); return true; } catch (e) { return false; } }
  // Bildquelle (URL, data URL oder HTMLImageElement) -> Promise<data URL> der simulierten Fassung, gecacht. max = laengste Kante in px.
  const BILDER = new Map();
  function bild(quelle, max) {
    const key = (typeof quelle === 'string' ? quelle : quelle.src) + '|' + (max || 1600);
    if (BILDER.has(key)) return BILDER.get(key);
    const pr = new Promise((res) => {
      const los = (im) => {
        let w = im.naturalWidth || im.width || 0, h = im.naturalHeight || im.height || 0; if (!w || !h) { w = 1200; h = 1200; }
        const s = Math.min(1, (max || 1600) / Math.max(w, h)), cv = document.createElement('canvas'); cv.width = Math.max(1, Math.round(w * s)); cv.height = Math.max(1, Math.round(h * s));
        try { cv.getContext('2d').drawImage(im, 0, 0, cv.width, cv.height); } catch (e) { return res(null); }
        res(canvas(cv) ? cv.toDataURL('image/png') : null);
      };
      if (typeof quelle !== 'string') { if (quelle.complete && (quelle.naturalWidth || quelle.width)) return los(quelle); quelle.addEventListener('load', () => los(quelle), { once: true }); quelle.addEventListener('error', () => res(null), { once: true }); return; }
      const im = new Image(); if (!/^data:|^blob:/.test(quelle)) im.crossOrigin = 'anonymous'; im.onload = () => los(im); im.onerror = () => res(null); im.src = quelle;
    });
    BILDER.set(key, pr); return pr;
  }
  // Synchroner Zugriff: fertige Fassung oder null (und Berechnung anstossen, fertig() wird danach einmal aufgerufen)
  const FERTIG = new Map();
  function bildSofort(quelle, fertig, max) {
    const key = (typeof quelle === 'string' ? quelle : quelle.src) + '|' + (max || 1600);
    if (FERTIG.has(key)) return FERTIG.get(key);
    bild(quelle, max).then((u) => { if (FERTIG.has(key)) return; FERTIG.set(key, u); if (fertig) fertig(u); });
    return null;
  }

  // ---------------------------------------------------------------- Aufloesung
  const ZIEL_DPI = 300;
  const dpi = (px, mm) => (px && mm ? Math.round(px / (mm / 25.4)) : null);
  const stufe = (d, vektor) => (vektor ? 'vektor' : d == null ? 'unbekannt' : d >= ZIEL_DPI ? 'ok' : d >= ZIEL_DPI / 2 ? 'knapp' : 'zu_gering');
  const maxMm = (px) => Math.floor(px / ZIEL_DPI * 25.4 * 10) / 10;

  // ---------------------------------------------------------------- Schriften (Google Fonts, OFL, als Theme Assets)
  const SCHRIFTEN = [
    { id: 'montserrat', name: 'Montserrat', gewicht: 700, art: 'Serifenlos', fallback: 'Arial, sans-serif' },
    { id: 'poppins', name: 'Poppins', gewicht: 700, art: 'Serifenlos', fallback: 'Arial, sans-serif' },
    { id: 'roboto', name: 'Roboto', gewicht: 700, art: 'Serifenlos', fallback: 'Arial, sans-serif' },
    { id: 'open-sans', name: 'Open Sans', gewicht: 700, art: 'Serifenlos', fallback: 'Arial, sans-serif' },
    { id: 'lato', name: 'Lato', gewicht: 700, art: 'Serifenlos', fallback: 'Arial, sans-serif' },
    { id: 'nunito', name: 'Nunito', gewicht: 700, art: 'Serifenlos', fallback: 'Arial, sans-serif' },
    { id: 'raleway', name: 'Raleway', gewicht: 700, art: 'Serifenlos', fallback: 'Arial, sans-serif' },
    { id: 'oswald', name: 'Oswald', gewicht: 600, art: 'Serifenlos', fallback: '"Arial Narrow", sans-serif' },
    { id: 'bebas-neue', name: 'Bebas Neue', gewicht: 400, art: 'Display', fallback: 'Impact, sans-serif' },
    { id: 'abril-fatface', name: 'Abril Fatface', gewicht: 400, art: 'Display', fallback: 'Georgia, serif' },
    { id: 'mountains-of-christmas', name: 'Mountains of Christmas', gewicht: 700, art: 'Display', fallback: 'Georgia, serif' },
    { id: 'playfair-display', name: 'Playfair Display', gewicht: 700, art: 'Serif', fallback: 'Georgia, serif' },
    { id: 'merriweather', name: 'Merriweather', gewicht: 700, art: 'Serif', fallback: 'Georgia, serif' },
    { id: 'lora', name: 'Lora', gewicht: 700, art: 'Serif', fallback: 'Georgia, serif' },
    { id: 'libre-baskerville', name: 'Libre Baskerville', gewicht: 700, art: 'Serif', fallback: 'Georgia, serif' },
    { id: 'courier-prime', name: 'Courier Prime', gewicht: 700, art: 'Serif', fallback: '"Courier New", monospace' },
    { id: 'dancing-script', name: 'Dancing Script', gewicht: 700, art: 'Schreibschrift', fallback: 'cursive' },
    { id: 'pacifico', name: 'Pacifico', gewicht: 400, art: 'Schreibschrift', fallback: 'cursive' },
    { id: 'great-vibes', name: 'Great Vibes', gewicht: 400, art: 'Schreibschrift', fallback: 'cursive' },
    { id: 'caveat', name: 'Caveat', gewicht: 700, art: 'Schreibschrift', fallback: 'cursive' },
    { id: 'lobster', name: 'Lobster', gewicht: 400, art: 'Schreibschrift', fallback: 'cursive' }
  ];
  SCHRIFTEN.forEach((s) => { s.familie = `"${s.name}", ${s.fallback}`; s.datei = `vp-font-${s.id}.woff2`; });
  const schrift = (idOderFamilie) => SCHRIFTEN.find((s) => s.id === idOderFamilie || s.familie === idOderFamilie || s.name === idOderFamilie) || null;
  // Asset Basis aus der eigenen Script URL (…/assets/vp-druckfarben.js?v=…)
  const eigen = document.currentScript || document.querySelector('script[src*="vp-druckfarben"]');
  const BASIS = eigen && eigen.src ? eigen.src.replace(/vp-druckfarben\.js.*$/, '') : '';
  const url = (s) => BASIS + s.datei;
  const GELADEN = new Map();
  function laden(ids) {
    const liste = (ids ? [].concat(ids).map(schrift).filter(Boolean) : SCHRIFTEN);
    return Promise.all(liste.map((s) => {
      if (GELADEN.has(s.id)) return GELADEN.get(s.id);
      let p;
      try { const ff = new FontFace(s.name, `url(${url(s)}) format("woff2")`, { weight: String(s.gewicht), display: 'swap' }); document.fonts.add(ff); p = ff.load().then(() => true, () => false); } catch (e) { p = Promise.resolve(false); }
      GELADEN.set(s.id, p); return p;
    }));
  }
  // @font-face mit eingebetteter Datei fuer eigenstaendige SVGs (ein SVG als Bild laedt keine externen Schriften)
  const EINGEBETTET = new Map();
  function fontFaceCss(ids) {
    const liste = [].concat(ids || []).map(schrift).filter(Boolean);
    return Promise.all(liste.map((s) => {
      if (!EINGEBETTET.has(s.id)) EINGEBETTET.set(s.id, fetch(url(s)).then((r) => (r.ok ? r.arrayBuffer() : null)).then((buf) => {
        if (!buf) return ''; let bin = ''; const u = new Uint8Array(buf); for (let i = 0; i < u.length; i += 0x8000) bin += String.fromCharCode.apply(null, u.subarray(i, i + 0x8000));
        return `@font-face{font-family:"${s.name}";font-weight:${s.gewicht};src:url(data:font/woff2;base64,${btoa(bin)}) format("woff2")}`;
      }).catch(() => ''));
      return EINGEBETTET.get(s.id);
    })).then((teile) => teile.join(''));
  }

  return { hex, info, cmyk, cmykText, bild, bildSofort, canvas, pixel, dpi, stufe, maxMm, ZIEL_DPI, SCHRIFTEN, schrift, laden, fontFaceCss, _abbilden: abbilden, _druckOk: druckOk, _rgbOk: rgbOk, _okRgb: okRgb };
})();
